import type { AxiosInstance } from 'axios';
import type { IAllProps } from '@tinymce/tinymce-react';
import { MAIL_FILE_ACCEPT, mailFileLinkHtml, mailFileProblem, type MailFile } from '@malash-office/types';

/** The editor `setup` receives — from tinymce-react's own copy of TinyMCE, not the root one. */
type EditorLike = Parameters<NonNullable<NonNullable<IAllProps['init']>['setup']>>[0];

/** Where every file the editor holds is stored (`POST /api/pvt/editor-files`). */
export const EDITOR_FILES_URL = 'pvt/editor-files';

/** The server's Hebrew reason when there is one. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const uploadErrorMessage = (error: any): string =>
  error?.response?.data?.message || error?.response?.data?.error || 'העלאת הקובץ נכשלה';

const pickFile = (): Promise<File | null> =>
  new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = MAIL_FILE_ACCEPT;
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });

/**
 * Files go to the bucket and into the body — an image as an image, anything else as a link with
 * its name and size. That is the only way a file can travel in a Smoove mail (it cannot attach),
 * and since the body is what templates, history and resends keep, the file travels with all of
 * them. One by one, in order, so the links land in the order they were dropped.
 */
const insertFiles = async (editor: EditorLike, http: AxiosInstance, files: File[]) => {
  const fail = (text: string) => editor.notificationManager.open({ text, type: 'error', timeout: 6000 });
  editor.setProgressState(true);
  try {
    for (const file of files) {
      const problem = mailFileProblem(file);
      if (problem) {
        fail(`${file.name}: ${problem}`);
        continue;
      }
      const form = new FormData();
      form.append('file', file);
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await http.post<MailFile>(EDITOR_FILES_URL, form);
        editor.insertContent(
          data.mimeType.startsWith('image/')
            ? `<img src="${data.url}" alt="${data.name.replace(/"/g, '&quot;')}" />`
            : `<p>${mailFileLinkHtml(data)}</p>`
        );
      } catch (error) {
        fail(`${file.name}: ${uploadErrorMessage(error)}`);
      }
    }
  } finally {
    editor.setProgressState(false);
  }
};

/** "צירוף קובץ" (📎). */
export const registerAttachFileButton = (editor: EditorLike, http: AxiosInstance) => {
  editor.ui.registry.addButton('attachfile', {
    icon: 'upload',
    tooltip: 'צירוף קובץ (PDF, אקסל, וורד, תמונה, וידאו, שמע)',
    onAction: async () => {
      const file = await pickFile();
      if (file) await insertFiles(editor, http, [file]);
    }
  });
};

/**
 * Dragging or pasting a file into the editor does what 📎 does. TinyMCE takes only images on its
 * own and answers anything else with "סוג הקובץ המוטל אינו נתמך"; claiming the event first
 * (`preventDefault`) is what its blocker checks for. A drop of images only is left to TinyMCE,
 * which uploads them through `images_upload_handler` to the same route.
 */
export const registerFileDrop = (editor: EditorLike, http: AxiosInstance) => {
  const claim = (e: Event, transfer: DataTransfer | null, at?: { x: number; y: number }) => {
    const files = Array.from(transfer?.files ?? []);
    if (!files.length || files.every(file => file.type.startsWith('image/'))) return;
    e.preventDefault();
    if (at) editor.selection.placeCaretAt(at.x, at.y);
    void insertFiles(editor, http, files);
  };
  editor.on('drop', e => claim(e, e.dataTransfer, { x: e.clientX, y: e.clientY }));
  editor.on('paste', e => claim(e, e.clipboardData));
};
